import { API_BASE_URL, httpBlob, httpRequest } from './http.js'

async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  return httpRequest<T>(path, opts)
}
const get  = <T>(p: string)              => apiFetch<T>(p)
const post = <T>(p: string, b?: unknown) => apiFetch<T>(p, { method: 'POST', body: b ? JSON.stringify(b) : undefined })
const qs   = (p: object) => { const e = Object.entries(p as Record<string, unknown>).filter(([,v]) => v !== undefined && v !== null && v !== ''); return e.length ? '?' + e.map(([k,v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&') : '' }

async function downloadBlob(path: string): Promise<Blob> {
  return httpBlob(path)
}

interface Paginated<T> {
  data: T[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

// ─── AsRun ───────────────────────────────────────────────────────────────────

/** Spot figé d'un bloc pub dans l'instantané as-run. */
export interface AsRunSpot {
  order:          number
  spotTitle:      string
  campaignName:   string | null
  advertiserName: string | null
  durationSec:    number
}

export interface AsRunEntry {
  id:              string
  scheduleItemId:  string
  channelId:       string
  scheduledStart:  string
  actualStart:     string | null
  actualEnd:       string | null
  durationDiffSec: number | null
  sourceLabel:     string | null
  status:          string
  metadata:        Record<string, unknown> | null
  createdAt:       string
  // Détail figé des spots pour un bloc pub (itemType AD_BREAK), sinon null.
  spotsSnapshot:   AsRunSpot[] | null
  // Instantané figé à la diffusion : reste lisible après suppression du
  // programme au catalogue. À préférer systématiquement aux relations jointes.
  programId:            string | null
  programTitle:         string | null
  programCode:          string | null
  programType:          string | null
  itemType:             string
  scheduledDurationSec: number | null
  // Relations jointes
  scheduleItem?: {
    itemType:  string
    program?:  { title: string; type: string; durationSec: number; genre?: string | null }
    liveSource?: { name: string }
    interludeClip?: { name: string }
  }
}

export interface ReportStats {
  totals: {
    count: number
    totalActualSec: number
    totalScheduledSec: number
    okCount: number
    errorCount: number
    avgAbsDriftSec: number
  }
  byProgramType: Record<string, {
    count: number
    actualSec: number
    scheduledSec: number
    avgAbsDriftSec: number
    errorCount: number
  }>
  byItemType: Record<string, { count: number; actualSec: number }>
}

export interface ReportQuery {
  channelId?: string
  from?: string
  to?: string
  status?: string
  itemType?: string
  programType?: string
  includeTechnicalTests?: boolean
  limit?: number
  page?: number
}

export const asRunApi = {
  list: async (q: ReportQuery) => {
    const res = await get<Paginated<AsRunEntry>>(`/asrun${qs(q)}`)
    return res.data
  },
  stats: (q: ReportQuery) =>
    get<ReportStats>(`/asrun/stats${qs(q)}`),
  export: (q: { channelId: string; from: string; to: string; status?: string; itemType?: string; programType?: string; includeTechnicalTests?: boolean; format: 'json' | 'csv' }) =>
    get<any>(`/asrun/export${qs(q)}`),
  downloadPdf: (q: { channelId: string; from: string; to: string; status?: string; itemType?: string; programType?: string; includeTechnicalTests?: boolean }) =>
    downloadBlob(`/asrun/report.pdf${qs(q)}`),

  /** Chiffre ce que la purge détruirait, sans rien supprimer. */
  purgePreview: (q: { channelId: string; from: string; to: string }) =>
    get<PurgePreview>(`/asrun/purge${qs(q)}`),

  /**
   * Purge effective. Les compteurs issus de l'aperçu sont renvoyés tels quels :
   * le serveur refuse (409) si la période a changé entre-temps.
   */
  purge: (body: { channelId: string; from: string; to: string; expectedAsRunCount: number; expectedScheduleItemCount: number }) =>
    post<{ deletedAsRunLogs: number; deletedScheduleItems: number }>('/asrun/purge', body),
}

export interface PurgePreview {
  asRunCount:        number
  scheduleItemCount: number
  oldest:            string | null
  newest:            string | null
}

// ─── Attestations de diffusion ────────────────────────────────────────────────

/** Modèle d'attestation propre à une chaîne (textes + présence des images). */
export interface AttestationTemplate {
  directorName:  string
  directorTitle: string
  city:          string
  introText:     string
  hasLogo:       boolean
  hasSignature:  boolean
}

export type AttestationImageKind = 'logo' | 'signature'

export const attestationApi = {
  getTemplate: (channelId: string) =>
    get<AttestationTemplate>(`/attestation/template${qs({ channelId })}`),

  saveTemplate: (channelId: string, patch: Partial<Pick<AttestationTemplate, 'directorName' | 'directorTitle' | 'city' | 'introText'>>) =>
    apiFetch<AttestationTemplate>(`/attestation/template${qs({ channelId })}`, { method: 'PUT', body: JSON.stringify(patch) }),

  uploadImage: (channelId: string, kind: AttestationImageKind, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return apiFetch<{ fileName: string }>(`/attestation/template/${kind}${qs({ channelId })}`, { method: 'POST', body: form })
  },

  deleteImage: (channelId: string, kind: AttestationImageKind) =>
    apiFetch<void>(`/attestation/template/${kind}${qs({ channelId })}`, { method: 'DELETE' }),

  /** URL de prévisualisation d'une image (cache court, sert d'aperçu direct). */
  imageUrl: (channelId: string, kind: AttestationImageKind) =>
    `${API_BASE_URL}/attestation/template/${kind}${qs({ channelId })}`,

  /** Attestation pour un ou plusieurs programmes réellement diffusés. */
  downloadProgramsPdf: (q: { channelId: string; programIds: string[]; from?: string; to?: string }) =>
    downloadBlob(`/attestation/programs.pdf${qs({ channelId: q.channelId, programIds: q.programIds.join(','), from: q.from, to: q.to })}`),

  /** Attestation publicitaire pour une campagne ou une liste de spots. */
  downloadAdsPdf: (q: { channelId: string; campaignId?: string; spotIds?: string[]; from?: string; to?: string }) =>
    downloadBlob(`/attestation/ads.pdf${qs({ channelId: q.channelId, campaignId: q.campaignId, spotIds: q.spotIds?.join(','), from: q.from, to: q.to })}`),
}

// ─── Gap Events ──────────────────────────────────────────────────────────────

export interface GapEventEntry {
  id:              string
  channelId:       string
  triggeredAt:     string
  gapSec:          number
  scenario:        string
  strategy:        string
  resolvedAt:      string | null
  autoResolved:    boolean
  resolutionNote:  string | null
}

export const gapEventsApi = {
  list: async (q: { channelId?: string; from?: string; to?: string }) => {
    const res = await get<Paginated<GapEventEntry>>(`/gap-events${qs(q)}`)
    return res.data
  },
}

// ─── Watchdog checks ─────────────────────────────────────────────────────────

export interface WatchdogCheckEntry {
  id:             string
  scheduleItemId: string
  checkedAt:      string
  checkType:      string
  severity:       string
  driftSec:       number | null
  actionTaken:    string
}

export const watchdogApi = {
  listChecks: (q: { channelId?: string; severity?: string; from?: string; to?: string }) =>
    get<WatchdogCheckEntry[]>(`/watchdog-checks${qs(q)}`),
}

// ─── CSA stats (calculés côté client depuis AsRun) ───────────────────────────

export interface CsaStats {
  totalDiffusedMin:   number
  byType:             Record<string, number>   // type → minutes
  gapCount:           number
  totalGapSec:        number
  avgDriftSec:        number
  coveragePct:        number
}

/**
 * Résolution des libellés de rapport : l'instantané prime toujours sur la
 * jointure, seule façon d'afficher un programme retiré du catalogue.
 */
export function asRunTitle(log: AsRunEntry): string | null {
  return log.programTitle
    ?? log.scheduleItem?.program?.title
    ?? log.scheduleItem?.liveSource?.name
    ?? log.scheduleItem?.interludeClip?.name
    ?? null
}

export function asRunProgramType(log: AsRunEntry): string | null {
  return log.programType ?? log.scheduleItem?.program?.type ?? null
}

export function asRunItemType(log: AsRunEntry): string {
  return log.itemType ?? log.scheduleItem?.itemType ?? 'UNKNOWN'
}

/** Spots figés d'un bloc pub, ordonnés ; tableau vide pour les autres items. */
export function asRunSpots(log: AsRunEntry): AsRunSpot[] {
  return Array.isArray(log.spotsSnapshot) ? log.spotsSnapshot : []
}

export function computeCsaStats(logs: AsRunEntry[]): CsaStats {
  const totalSec = logs.reduce((acc, l) => {
    if (!l.actualStart || !l.actualEnd) return acc
    return acc + Math.round((new Date(l.actualEnd).getTime() - new Date(l.actualStart).getTime()) / 1000)
  }, 0)

  const byType: Record<string, number> = {}
  for (const l of logs) {
    const type = asRunItemType(l)
    const sec  = l.actualStart && l.actualEnd
      ? Math.round((new Date(l.actualEnd).getTime() - new Date(l.actualStart).getTime()) / 1000)
      : 0
    byType[type] = (byType[type] ?? 0) + sec
  }

  const gaps   = logs.filter(l => l.status !== 'OK')
  const drifts = logs.filter(l => l.durationDiffSec !== null).map(l => l.durationDiffSec!)
  const avgDrift = drifts.length ? Math.round(drifts.reduce((a, b) => a + Math.abs(b), 0) / drifts.length) : 0

  return {
    totalDiffusedMin: Math.round(totalSec / 60),
    byType:           Object.fromEntries(Object.entries(byType).map(([k, v]) => [k, Math.round(v / 60)])),
    gapCount:         gaps.length,
    totalGapSec:      gaps.reduce((acc, l) => acc + Math.abs(l.durationDiffSec ?? 0), 0),
    avgDriftSec:      avgDrift,
    coveragePct:      Math.min(100, Math.round((totalSec / (24 * 3600)) * 100)),
  }
}
